import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { RecordRefSchema } from './notification.ts';

/**
 * A warranty claim on a finished job (SCRUM-140, P8-01).
 *
 * `docs/features/13-later-modules.md`: "Owner submits claim (title,
 * category, description, photos); GC assigns sub, schedules service
 * appointments, closes with notes; status list; portal view."
 *
 * **Job-scoped in the path**, like to-dos and daily logs and for the same
 * reason: a `list` rule can then enforce job assignment with one job read
 * rather than one per returned row.
 *
 * This is the first module where the **homeowner creates the record**.
 * Everywhere else an external member answers something somebody else
 * raised — a sub accepts dates, an owner approves a change order. Here the
 * owner opens the conversation, which is why `submittedBy` is kept and
 * never recomputed: it is the claim's provenance, and a claim whose author
 * could change is a claim nobody can audit.
 */
export const WARRANTY_CATEGORIES = [
  'plumbing',
  'electrical',
  'hvac',
  'roofing',
  'structural',
  'finishes',
  'appliances',
  'siteworks',
  'other',
] as const;
export const WarrantyCategorySchema = z.enum(WARRANTY_CATEGORIES);
export type WarrantyCategory = z.infer<typeof WarrantyCategorySchema>;

export const WARRANTY_CATEGORY_LABEL: Record<WarrantyCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'Heating and cooling',
  roofing: 'Roofing',
  structural: 'Structural',
  finishes: 'Finishes',
  appliances: 'Appliances',
  siteworks: 'Site works',
  other: 'Something else',
};

/**
 * Where a claim is.
 *
 * **`scheduled` is deliberately not one of these.** A claim with a future
 * appointment on it *is* scheduled, and storing that as a status would
 * give two sources for one fact — exactly the `status` + `current_job_id`
 * duplication that was dropped from the equipment module. `isScheduled`
 * below derives it.
 *
 * `declined` is terminal and demands a reason: telling a homeowner their
 * claim is not covered without saying why is how a warranty becomes a
 * dispute.
 */
export const WARRANTY_STATUSES = [
  'submitted',
  'accepted',
  'declined',
  'resolved',
  'closed',
] as const;
export const WarrantyStatusSchema = z.enum(WARRANTY_STATUSES);
export type WarrantyStatus = z.infer<typeof WarrantyStatusSchema>;

export const WARRANTY_STATUS_LABEL: Record<WarrantyStatus, string> = {
  submitted: 'Submitted',
  accepted: 'Accepted',
  declined: 'Not covered',
  resolved: 'Fixed',
  closed: 'Closed',
};

/**
 * Newest first, with the tie broken the way Firestore breaks it.
 *
 * `submittedAt` alone is not a total order, and the gap is not
 * theoretical: two claims reported in the same millisecond tie, and a tie
 * in `orderBy` leaves the adapter free to return either first. Real
 * Firestore falls back to the document key, and ids are ULIDs — so key
 * order *is* submission order, and `id` descending reproduces exactly
 * what the query returns rather than a second opinion about it. The same
 * reasoning is recorded on `mergeScheduleItems`, for the same reason: a
 * sort that disagrees with the query it decorates is a reordering nobody
 * asked for.
 *
 * Found by the e2e suite, which pins the browser clock — so every claim a
 * test reports carries an identical timestamp and the tie is the normal
 * case rather than a rare one. It passed alone and failed in the full run.
 */
export function newestClaimsFirst(claims: readonly WarrantyClaim[]): WarrantyClaim[] {
  return [...claims].sort(
    (a, b) => b.submittedAt.localeCompare(a.submittedAt) || b.id.localeCompare(a.id),
  );
}

/**
 * A visit to look at or fix the problem.
 *
 * Held on the claim rather than in a subcollection, like a to-do's
 * checklist: the list is always read with the claim, it is short, and one
 * field means two people adding an appointment get a detected conflict
 * instead of a list that silently lost one.
 *
 * A date and a note, not a schedule item. A warranty visit is an hour on
 * somebody's Tuesday, not a bar on the job's Gantt — and the job is
 * finished, so its schedule is history.
 */
export const WarrantyAppointmentSchema = z.object({
  id: IdSchema,
  on: DateOnlySchema,
  note: z.string().trim().max(500).default(''),
  /** Who is going, as a directory contact. Optional: it may not be settled. */
  contactId: IdSchema.optional(),
});
export type WarrantyAppointment = z.infer<typeof WarrantyAppointmentSchema>;

export const MAX_APPOINTMENTS = 50;

/**
 * What a trade reports after going out to a claim (P8-03b).
 *
 * **A document of its own, not a field on the claim**, and the reason is
 * the one that put a purchase order's internal notes and a package's
 * bids in siblings: Firestore rules are all-or-nothing per document. A
 * trade writing onto the claim would be writing the document that holds
 * the status, the resolution and the owner's own words, and the only
 * defence would be a rule freezing every other field one by one — which
 * is a list that silently stops being complete the next time somebody
 * adds a field to the claim.
 *
 * So the trade writes here, and the claim stays the office's.
 *
 * **It records what happened, never what it means.** `status` is the
 * office's and so is `resolution`: a claim a trade could mark resolved
 * themselves records nothing anybody else agreed to, which is the same
 * reasoning that keeps an RFI's answer out of the asker's hands. A
 * builder reading "replaced the valve, left it running" still has to
 * decide whether the claim is closed.
 */
export const WarrantyVisitReportSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  claimId: IdSchema,
  /** Whose report it is, as a directory contact. Never from the request. */
  contactId: IdSchema,
  /** The day they went. A calendar date, like an appointment's. */
  happenedOn: DateOnlySchema,
  /**
   * What was done. Required and non-empty: a visit report with nothing
   * in it is a claim that somebody attended, which is the one thing an
   * owner will later dispute.
   */
  notes: z.string().trim().min(1, 'Say what was done').max(5000),
});
export type WarrantyVisitReport = z.infer<typeof WarrantyVisitReportSchema>;

/** Most recent visit first, which is the one anybody is asking about. */
export function newestVisitsFirst(reports: readonly WarrantyVisitReport[]): WarrantyVisitReport[] {
  return [...reports].sort(
    (a, b) => b.happenedOn.localeCompare(a.happenedOn) || b.id.localeCompare(a.id),
  );
}

export const WarrantyClaimSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().trim().min(1).max(200),
  category: WarrantyCategorySchema,
  /** Plain text until there is a sanitiser, as for daily log notes. */
  description: z.string().max(20_000).default(''),
  status: WarrantyStatusSchema.default('submitted'),

  /**
   * Who raised it, kept for ever.
   *
   * `createdBy` on `AuditSchema` would say the same thing today, and will
   * not when somebody files a claim on an owner's behalf after a phone
   * call — which is most of them. So the two are separate from the start.
   */
  submittedBy: IdSchema,
  submittedAt: InstantSchema,

  /**
   * The sub sent to fix it, as a directory contact.
   *
   * A contact rather than a member, because the trade who installed the
   * roof is in the directory whether or not they have a login — and the
   * rules need `assignedContactId` as a scalar to gate a sub's access on.
   */
  assignedContactId: IdSchema.optional(),

  appointments: z.array(WarrantyAppointmentSchema).max(MAX_APPOINTMENTS).default([]),

  /** Why it was declined, or how it was fixed. Both are owed an answer. */
  resolution: z
    .object({
      notes: z.string().trim().min(1).max(5000),
      at: InstantSchema,
      byUid: IdSchema,
    })
    .optional(),

  linkedRecords: z.array(RecordRefSchema).default([]),
  linkedKeys: z.array(z.string()).default([]),
});
export type WarrantyClaim = z.infer<typeof WarrantyClaimSchema>;

/**
 * Whether a visit is booked that has not happened yet.
 *
 * Derived rather than stored, which is the point: two places that both
 * claim to know whether a claim is scheduled will eventually disagree, and
 * the one somebody reads will be the wrong one.
 */
export function isClaimScheduled(
  claim: Pick<WarrantyClaim, 'status' | 'appointments'>,
  today: string,
): boolean {
  if (claim.status !== 'accepted') return false;
  return claim.appointments.some((appointment) => appointment.on >= today);
}

/** The next visit, or nothing. Earliest first, past ones ignored. */
export function nextAppointment(
  claim: Pick<WarrantyClaim, 'appointments'>,
  today: string,
): WarrantyAppointment | null {
  return (
    [...claim.appointments]
      .filter((appointment) => appointment.on >= today)
      .sort((a, b) => a.on.localeCompare(b.on))[0] ?? null
  );
}

/** Still somebody's problem: not declined, not closed. */
export function isClaimOpen(claim: Pick<WarrantyClaim, 'status'>): boolean {
  return claim.status !== 'declined' && claim.status !== 'closed';
}

/**
 * Which statuses a claim may move to from where it is.
 *
 * A table rather than a chain of `if`s, so the shape of the lifecycle is
 * readable in one place and a screen can ask what to offer rather than
 * hard-coding buttons.
 *
 * Two decisions worth keeping. **`declined` and `closed` are terminal**: a
 * claim that could be reopened would make "closed" mean "closed for now",
 * and a homeowner with a new problem should raise a new claim that carries
 * its own dates. And **`resolved` can go back to `accepted`** — the fix did
 * not hold, which is the single most likely thing to happen to a warranty
 * repair, and making somebody file a fresh claim for it would lose the
 * history of the first visit.
 */
export const WARRANTY_TRANSITIONS: Readonly<Record<WarrantyStatus, readonly WarrantyStatus[]>> = {
  submitted: ['accepted', 'declined'],
  accepted: ['resolved', 'declined'],
  resolved: ['closed', 'accepted'],
  declined: [],
  closed: [],
};

/**
 * Named for warranties rather than `canTransition`, which change orders
 * already export into the shared namespace. Two lifecycles, two tables,
 * and a name that says which.
 */
export function canTransitionClaim(from: WarrantyStatus, to: WarrantyStatus): boolean {
  return WARRANTY_TRANSITIONS[from].includes(to);
}

/** Whether moving to this status owes somebody an explanation. */
export function claimNeedsReason(to: WarrantyStatus): boolean {
  // Declining tells a homeowner their claim is not covered, and resolving
  // tells them it is fixed. Both are answers they are entitled to have in
  // words rather than as a badge changing colour.
  return to === 'declined' || to === 'resolved';
}
